import {
  ITINERARY_MAX_DAYS,
  ITINERARY_MIN_DAYS,
  ITINERARY_TIME_SLOTS,
} from "../config";
import type {
  DayCountChange,
  IdFactory,
  ItineraryDay,
  ItineraryEditor,
  ItineraryNode,
  ItineraryParallelTrack,
  ItinerarySegment,
  ItinerarySourcePayload,
  ItineraryValidationIssue,
  MovePointTarget,
  NodeDraft,
  RailwayPairDraft,
  TimeSlot,
  TransportSelection,
} from "../types";
import { deleteProfessionalNode, getItineraryProfessionalSettings, insertProfessionalNode, moveProfessionalNode, setItineraryProfessionalSettings } from "./itineraryProfessionalService";
import { preserveRailwayTransferAfterSlotMove } from "./itineraryRailwayTransferService";
import { normalizeRailwayOvernightStays, railwayOvernightStayIds } from "./itineraryRailwayOvernightService";

/** 识别系统内两类受控待确认交通来源，不把 provider 来源误判为占位。 */
export function isPendingTransportSource(sourceType?: string | null): boolean {
  return sourceType === "pending" || sourceType === "inherited_pending";
}

type DayEndpointNode = Pick<ItineraryNode, "nodeType" | "title">
  & Partial<Pick<ItineraryNode, "pointSpuId" | "stayAreaId" | "organizationSiteId">>;

const validationTimeSlotLabels: Record<TimeSlot, string> = {
  morning: "上午",
  afternoon: "下午",
  evening: "晚上",
};

/** 按调用方给定的业务路径提取首尾地点；同一资源地点只保留一次。 */
export function summarizeDayEndpoints<T extends {
  nodeType: DayEndpointNode["nodeType"];
  title: DayEndpointNode["title"];
  pointSpuId?: DayEndpointNode["pointSpuId"];
  stayAreaId?: DayEndpointNode["stayAreaId"];
  organizationSiteId?: DayEndpointNode["organizationSiteId"];
}>(nodes: readonly T[]) {
  const first = nodes[0];
  const last = nodes.at(-1);
  if (!first || !last) return null;
  const firstTitle = first.title.trim();
  const lastTitle = last.title.trim();
  const isSameLocation = isSameEndpointLocation(first, last);
  return {
    first,
    last,
    isSameLocation,
    label: isSameLocation ? firstTitle : `${firstTitle} → ${lastTitle}`,
  };
}

/** 专业日按首条轨道显式路径取点，普通日按编辑器业务顺序取点。 */
export function resolveDayEndpointNodes(day: ItineraryDay): ItineraryNode[] {
  const firstTrackNodeIds = day.parallelTracks?.tracks[0]?.nodeIds;
  if (firstTrackNodeIds?.length) {
    const nodesById = new Map(day.nodes.map((node) => [node.id, node]));
    const trackNodes = firstTrackNodeIds
      .map((nodeId) => nodesById.get(nodeId))
      .filter((node): node is ItineraryNode => node !== undefined);
    if (trackNodes.length) return trackNodes;
  }
  return [...day.nodes].sort((left, right) =>
    ITINERARY_TIME_SLOTS.indexOf(left.timeSlot) - ITINERARY_TIME_SLOTS.indexOf(right.timeSlot)
    || left.sortOrder - right.sortOrder
    || left.id.localeCompare(right.id),
  );
}

/** 学校校区出发但末日未以学校校区结束时，人工保存需再次确认。 */
export function requiresSchoolReturnConfirmation(editor: ItineraryEditor): boolean {
  const days = [...editor.days].sort((left, right) => left.dayNumber - right.dayNumber);
  const firstDay = days[0];
  const lastDay = days.at(-1);
  if (!firstDay || !lastDay) return false;
  return resolveDayEndpointNodes(firstDay)[0]?.nodeType === "organization_site"
    && resolveDayEndpointNodes(lastDay).at(-1)?.nodeType !== "organization_site";
}

/** 住宿与相邻日点位/学校之间只属于住宿衔接，不按普通出行段展示交通详情。 */
export function isStayPointConnection(fromNodeType?: string | null, toNodeType?: string | null): boolean {
  const pair = new Set([fromNodeType, toNodeType]);
  return pair.has("stay_area") && ["point", "custom", "organization_site"].some((kind) => pair.has(kind));
}

/** 按首尾住宿点或学校校区识别日标题，端点信息不足时不生成占位标题。 */
export function inferDayTitle(day: ItineraryDay): string | null {
  const endpoints = resolveDayEndpointNodes(day);
  const first = endpoints[0];
  const last = endpoints.at(-1);
  if (!first || !last
    || !["stay_area", "organization_site"].includes(first.nodeType)
    || !["stay_area", "organization_site"].includes(last.nodeType)
    || !first.title.trim() || !last.title.trim()) return null;
  return isSameEndpointLocation(first, last)
    ? first.title.trim()
    : `${first.title.trim()}-${last.title.trim()}`;
}

/** 根据出发日期与天次显示星期，使用 UTC 日历运算避免本地时区偏移。 */
export function formatDayWeekday(startDate: string | null | undefined, dayNumber: number): string {
  if (!startDate || dateByOffset(startDate, 0) !== startDate) return "";
  const dayDate = dateByOffset(startDate, dayNumber - 1);
  if (!dayDate) return "";
  return ["周日", "周一", "周二", "周三", "周四", "周五", "周六"][new Date(`${dayDate}T00:00:00Z`).getUTCDay()];
}

/** 从相邻日已确认的首尾住宿点补齐当前日住宿起终点，不创建新住宿主数据。 */
export function completeDayStayEndpoints(
  editor: ItineraryEditor,
  dayId: string,
  idFactory: IdFactory,
): ItineraryEditor {
  const targetDay = editor.days.find((day) => day.id === dayId);
  if (!targetDay) throw new Error("找不到当前行程日");
  const days = [...editor.days].sort((left, right) => left.dayNumber - right.dayNumber);
  const targetIndex = days.findIndex((day) => day.id === dayId);
  const previousStay = days.slice(0, targetIndex).reverse()
    .map((day) => resolveDayEndpointNodes(day).at(-1))
    .find((node) => node?.nodeType === "stay_area" && node.title.trim() !== "无");
  const nextStay = days.slice(targetIndex + 1)
    .map((day) => resolveDayEndpointNodes(day)[0])
    .find((node) => node?.nodeType === "stay_area" && node.title.trim() !== "无");
  const ownStay = targetDay.stayAreaName?.trim() && targetDay.stayAreaName.trim() !== "无" && targetDay.stayAreaId
    ? { nodeType: "stay_area" as const, stayAreaId: targetDay.stayAreaId, title: targetDay.stayAreaName.trim() }
    : undefined;
  let next = editor;
  const endpoints = resolveDayEndpointNodes(targetDay);
  const hadEndStay = endpoints.at(-1)?.nodeType === "stay_area";
  const addStay = (source: Pick<ItineraryNode, "title" | "stayAreaId">, timeSlot: TimeSlot, side: "start" | "end") => {
    const draft: NodeDraft = { nodeType: "stay_area", stayAreaId: source.stayAreaId ?? null, title: source.title };
    const nodeId = idFactory();
    let firstId = true;
    next = addNode(next, dayId, timeSlot, draft, () => {
      if (firstId) {
        firstId = false;
        return nodeId;
      }
      return idFactory();
    });
    if (side === "start") next = movePoint(next, nodeId, { dayId, timeSlot, index: 0 }, idFactory);
  };
  if (endpoints[0]?.nodeType !== "stay_area" && previousStay) addStay(previousStay, "morning", "start");
  const endStay = ownStay ?? nextStay;
  if (!hadEndStay && endStay) addStay(endStay, "evening", "end");
  const completedDay = next.days.find((day) => day.id === dayId);
  const dayTitle = completedDay && !completedDay.dayTitle?.trim() ? inferDayTitle(completedDay) : null;
  if (dayTitle) {
    next = {
      ...next,
      days: next.days.map((day) => day.id === dayId ? { ...day, dayTitle } : day),
    };
  }
  return next;
}

/** 比较两个首尾节点是否代表同一业务资源。 */
function isSameEndpointLocation(left: DayEndpointNode, right: DayEndpointNode): boolean {
  if (left.nodeType !== right.nodeType) return false;
  const leftResourceId = endpointResourceId(left);
  const rightResourceId = endpointResourceId(right);
  if (leftResourceId !== null || rightResourceId !== null) {
    return leftResourceId !== null && leftResourceId === rightResourceId;
  }
  return normalizeEndpointTitle(left.title) === normalizeEndpointTitle(right.title);
}

/** 提取节点类型对应的稳定主数据标识。 */
function endpointResourceId(node: DayEndpointNode): string | null {
  const value = node.nodeType === "stay_area"
    ? node.stayAreaId
    : node.nodeType === "organization_site"
      ? node.organizationSiteId
      : ["point", "railway_station", "airport"].includes(node.nodeType)
        ? node.pointSpuId
        : null;
  return value === null || value === undefined || value === "" ? null : String(value);
}

/** 规范化无主数据标识的地点标题。 */
function normalizeEndpointTitle(title: string): string {
  return title.normalize("NFKC").trim().replace(/\s+/g, " ").toLocaleLowerCase();
}

/** 判断路线是否仍需用户在编辑器中人工确认。 */
export function requiresRouteConfirmation(segment: ItinerarySegment): boolean {
  if (isPendingTransportSource(segment.sourceType)) return true;
  if (segment.transportMode === "walk" && segment.sourceType === "default_walk") return false;
  const selected = segment.sourcePayload?.selected;
  const routeCode = segment.transportMode === "rail" || segment.transportMode === "air"
    ? selected?.routeCode?.trim() || segment.routeCode?.trim()
    : null;
  // 来源名称和起终点文案不能证明交通已填写；卡片、侧栏和修复流程共用此判断。
  return ![
    routeCode, selected?.departureAt ?? segment.departureAt, selected?.arrivalAt ?? segment.arrivalAt,
    selected?.durationMinutes ?? segment.durationMinutes, selected?.distanceKm ?? segment.distanceKm,
  ].some((value) => value !== null && value !== undefined && String(value).trim() !== "");
}

const semanticFields = {
  editor: ["id", "projectId", "sourceDemandId", "proposalArticleId", "pricedArticleId", "title", "startDate", "endDate", "status", "stage", "thirdPartyItineraryAttachment", "markdownSource", "isConfirmed", "summary", "customerServiceStandards", "professionSettings", "customerNotices", "days", "segments"],
  day: ["id", "dayNumber", "dayTitle", "dayDate", "stayAreaId", "legacyCompatibility", "morningSummary", "morningSummarySource", "afternoonSummary", "afternoonSummarySource", "eveningSummary", "eveningSummarySource", "fullDaySummary", "fullDaySummarySource", "summary", "summarySource", "isFullDay", "parallelTracks", "nodes"],
  node: ["id", "timeSlot", "sortOrder", "nodeType", "pointSpuId", "stayAreaId", "organizationSiteId", "title", "description", "planStartAt", "planEndAt"],
  segment: ["id", "startDayId", "endDayId", "routeSeq", "majorId", "routeType", "transportMode", "fromNodeId", "toNodeId", "transportSpuId", "transportSkuId", "busSpuId", "busSkuId", "routeCode", "departureAt", "arrivalAt", "distanceKm", "durationMinutes", "segmentDescription", "sourceType", "sourcePayload", "remark"],
  parallelTracks: ["version", "tracks"],
  professionalSettings: ["mainMajorId", "majors"],
  professionalMajor: ["id", "name"],
  track: ["id", "title", "majorId", "nodeIds", "segmentIds"],
  sourcePayload: ["version", "selected", "backups", "toolResultSnapshot", "railwayTransfer", "railwayOvernight"],
  railwayTransfer: ["version", "groupId", "nodeIds", "generatedNodeIds", "segmentIds", "plans", "chainSignature"],
  selection: ["provider", "optionId", "label", "routeCode", "fromName", "toName", "fromStationId", "toStationId", "price", "seatClass", "cabinClass", "departureAt", "arrivalAt", "distanceKm", "durationMinutes"],
  toolSnapshot: ["provider", "queriedAt", "queryKey", "railwayQueryScope", "options", "rawDurationMinutes", "businessDurationMinutes", "tollsYuan", "tollDistanceKm", "isReference", "referenceDate", "warnings"],
} as const;

/** 按保存契约与后端 revision 规则生成稳定的编辑器语义指纹。 */
export function itineraryEditorSemanticFingerprint(editor: ItineraryEditor): string {
  const ordered = {
    ...editor,
    days: [...editor.days]
      .sort((left, right) => left.dayNumber - right.dayNumber || left.id.localeCompare(right.id))
      .map((day) => ({
        ...day,
        morningSummarySource: day.morningSummarySource ?? "ai_generated",
        afternoonSummarySource: day.afternoonSummarySource ?? "ai_generated",
        eveningSummarySource: day.eveningSummarySource ?? "ai_generated",
        summarySource: day.summarySource ?? "ai_generated",
        nodes: [...day.nodes].sort((left, right) =>
          ITINERARY_TIME_SLOTS.indexOf(left.timeSlot) - ITINERARY_TIME_SLOTS.indexOf(right.timeSlot)
          || left.sortOrder - right.sortOrder
          || left.id.localeCompare(right.id)),
      })),
    segments: [...editor.segments].sort((left, right) =>
      left.routeSeq - right.routeSeq
      || (left.majorId ?? "").localeCompare(right.majorId ?? "")
      || left.id.localeCompare(right.id)),
  };
  return JSON.stringify(normalizeSemanticValue(ordered));
}

/** 递归补齐空值并稳定对象键序。 */
function normalizeSemanticValue(value: unknown): unknown {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) return value.map(normalizeSemanticValue);
  if (typeof value !== "object") return value;
  const record = value as Record<string, unknown>;
  const keys = semanticObjectFields(record) ?? Object.keys(record);
  return Object.fromEntries(
    [...keys]
      .sort()
      .map((key) => [key, normalizeSemanticValue(record[key])]),
  );
}

/** 按领域对象的必填特征返回保存契约中的已知字段。 */
function semanticObjectFields(value: Record<string, unknown>): readonly string[] | null {
  if ("days" in value && "segments" in value) return semanticFields.editor;
  if ("dayNumber" in value && "nodes" in value) return semanticFields.day;
  if ("timeSlot" in value && "sortOrder" in value) return semanticFields.node;
  if ("startDayId" in value && "routeSeq" in value) return semanticFields.segment;
  if ("groupId" in value && "plans" in value) return semanticFields.railwayTransfer;
  if ("nodeIds" in value && "segmentIds" in value) return semanticFields.track;
  if ("tracks" in value && "version" in value) return semanticFields.parallelTracks;
  if ("mainMajorId" in value && "majors" in value) return semanticFields.professionalSettings;
  if ("id" in value && "name" in value) return semanticFields.professionalMajor;
  if ("queriedAt" in value && "queryKey" in value) return semanticFields.toolSnapshot;
  if ("optionId" in value && "label" in value) return semanticFields.selection;
  if ("backups" in value && "version" in value) return semanticFields.sourcePayload;
  return null;
}

/** 判断路线是否携带班次、时刻、距离、说明或主备等已填写交通事实。 */
function hasFilledTransportFacts(segment: ItinerarySegment): boolean {
  return Boolean(
    segment.transportSpuId
    || segment.transportSkuId
    || segment.busSpuId
    || segment.busSkuId
    || segment.routeCode
    || segment.departureAt
    || segment.arrivalAt
    || segment.distanceKm
    || segment.durationMinutes
    || segment.segmentDescription?.trim()
    || segment.remark?.trim()
    || segment.sourcePayload?.selected
    || segment.sourcePayload?.backups?.length,
  );
}

/** 仅提取会因路线替换而丢失的交通事实，忽略段 ID、端点和排序号。 */
function transportFactsFingerprint(segment: ItinerarySegment): string {
  const payload = segment.sourcePayload;
  // 组签名只用于整链替换保护；受控重签不丢失主备、票价或其他已填写事实。
  const sourcePayload = payload?.railwayTransfer
    ? { ...payload, railwayTransfer: { ...payload.railwayTransfer, chainSignature: "" } }
    : payload ?? null;
  return JSON.stringify({
    transportSpuId: segment.transportSpuId ?? null,
    transportSkuId: segment.transportSkuId ?? null,
    busSpuId: segment.busSpuId ?? null,
    busSkuId: segment.busSkuId ?? null,
    routeCode: segment.routeCode ?? null,
    departureAt: segment.departureAt ?? null,
    arrivalAt: segment.arrivalAt ?? null,
    distanceKm: segment.distanceKm ?? null,
    durationMinutes: segment.durationMinutes ?? null,
    segmentDescription: segment.segmentDescription ?? null,
    sourceType: segment.sourceType ?? null,
    sourcePayload,
    remark: segment.remark ?? null,
  }, (_key, value: unknown) => value && typeof value === "object" && !Array.isArray(value)
    // 重新附加组可能改变对象键序；保留所有字段值和主备数组顺序参与比较。
    ? Object.fromEntries(Object.entries(value).sort(([left], [right]) => left.localeCompare(right)))
    : value);
}

/** 统计业务图重建后被替换的已填写交通路线数量。 */
export function countReplacedFilledTransportSegments(
  before: ItineraryEditor,
  after: ItineraryEditor,
  options: { exemptSegmentIds?: ReadonlySet<string> } = {},
): number {
  const afterById = new Map(after.segments.map((segment) => [segment.id, segment]));
  return before.segments.filter((segment) => {
    const replacement = afterById.get(segment.id);
    if (
      options.exemptSegmentIds?.has(segment.id)
      && replacement?.transportMode === "rail"
      && replacement.fromNodeId === segment.fromNodeId
      && replacement.toNodeId === segment.toNodeId
      && isPendingTransportSource(replacement.sourceType)
      && replacement.automaticQueryDepartureSlot
    ) return false;
    if (!hasFilledTransportFacts(segment)) return false;
    return !replacement || transportFactsFingerprint(segment) !== transportFactsFingerprint(replacement);
  }).length;
}

/** 设置行程出发日期，并按天数同步结束日期与每日日期。 */
export function setItineraryStartDate(
  editor: ItineraryEditor,
  startDate: string | null | undefined,
): ItineraryEditor {
  const next = structuredClone(editor);
  const normalized = startDate?.trim() || null;
  const dayDelta = dateDifference(editor.startDate ?? editor.days[0]?.dayDate, normalized);
  next.startDate = normalized;
  next.endDate = dateByOffset(normalized, next.days.length - 1);
  next.days = next.days.map((day, index) => ({
    ...day,
    dayDate: dateByOffset(normalized, index),
    nodes: day.nodes.map((node) => ({
      ...node,
      planStartAt: shiftDateTime(node.planStartAt, dayDelta),
      planEndAt: shiftDateTime(node.planEndAt, dayDelta),
    })),
  }));
  if (dayDelta !== null && dayDelta !== 0) {
    next.segments = next.segments.map((segment) => ({
      ...segment,
      departureAt: shiftDateTime(segment.departureAt, dayDelta),
      arrivalAt: shiftDateTime(segment.arrivalAt, dayDelta),
      sourcePayload: segment.sourcePayload
        ? {
            // 查询快照是原始来源证据，只平移主备方案的计划时刻。
            ...segment.sourcePayload,
            selected: shiftTransportSelection(segment.sourcePayload.selected, dayDelta),
            backups: segment.sourcePayload.backups.map((item) => shiftTransportSelection(item, dayDelta)),
          }
        : segment.sourcePayload,
    }));
  }
  return next;
}

/** 只移动完整行程日，并连续重排位置型天次与日期。 */
export function reorderDay(
  editor: ItineraryEditor,
  sourceDayId: string,
  targetDayId: string,
  placement: "before" | "after",
): ItineraryEditor {
  if (sourceDayId === targetDayId) return editor;
  requireDay(editor, sourceDayId);
  requireDay(editor, targetDayId);
  const next = structuredClone(editor);
  const sourceIndex = next.days.findIndex((day) => day.id === sourceDayId);
  const [movedDay] = next.days.splice(sourceIndex, 1);
  const targetIndex = next.days.findIndex((day) => day.id === targetDayId);
  next.days.splice(targetIndex + (placement === "after" ? 1 : 0), 0, movedDay);
  next.days = next.days.map((day, index) => ({
    ...day,
    dayNumber: index + 1,
    dayDate: dateByOffset(next.startDate, index),
  }));
  next.endDate = dateByOffset(next.startDate, next.days.length - 1);
  return next;
}

/** 调整行程天数，并返回需要 UI 确认的缩减信息。 */
export function changeDayCount(
  editor: ItineraryEditor,
  targetCount: number,
  idFactory: IdFactory,
): DayCountChange {
  if (!Number.isInteger(targetCount) || targetCount < ITINERARY_MIN_DAYS || targetCount > ITINERARY_MAX_DAYS) {
    throw new RangeError(`行程天数必须在 ${ITINERARY_MIN_DAYS}-${ITINERARY_MAX_DAYS} 之间`);
  }
  const professionalSettings = getItineraryProfessionalSettings(editor);
  const next = structuredClone(editor);
  const removedDayIds = next.days.slice(targetCount).map((day) => day.id);
  next.days = next.days.slice(0, targetCount);
  while (next.days.length < targetCount) {
    next.days.push({
      id: idFactory(),
      dayNumber: next.days.length + 1,
      dayDate: dateByOffset(next.startDate, next.days.length),
      isFullDay: false,
      nodes: [],
    });
  }
  next.days = next.days.map((day, index) => ({
    ...day,
    dayNumber: index + 1,
    dayDate: dateByOffset(next.startDate, index),
  }));
  next.endDate = dateByOffset(next.startDate, targetCount - 1);
  const keptDayIds = new Set(next.days.map((day) => day.id));
  next.segments = next.segments.filter((segment) => keptDayIds.has(segment.startDayId) && keptDayIds.has(segment.endDayId));
  return {
    next: withRebuiltGraph(professionalSettings ? setItineraryProfessionalSettings(next, professionalSettings, idFactory) : next, idFactory),
    requiresConfirmation: removedDayIds.length > 0,
    removedDayIds,
  };
}

/** 删除指定行程日并连续重排后续天次、日期与相邻路线。 */
export function removeDay(
  editor: ItineraryEditor,
  dayId: string,
  idFactory: IdFactory,
): ItineraryEditor {
  if (editor.days.length <= ITINERARY_MIN_DAYS) {
    throw new RangeError("行程至少保留 1 天");
  }
  requireDay(editor, dayId);
  const professionalSettings = getItineraryProfessionalSettings(editor);
  const next = structuredClone(editor);
  next.days = next.days
    .filter((day) => day.id !== dayId)
    .map((day, index) => ({
      ...day,
      dayNumber: index + 1,
      dayDate: dateByOffset(next.startDate, index),
    }));
  next.endDate = dateByOffset(next.startDate, next.days.length - 1);
  next.segments = next.segments.filter((segment) => segment.startDayId !== dayId && segment.endDayId !== dayId);
  return withRebuiltGraph(professionalSettings ? setItineraryProfessionalSettings(next, professionalSettings, idFactory) : next, idFactory);
}

/** 在指定时段追加节点。 */
export function addNode(
  editor: ItineraryEditor,
  dayId: string,
  timeSlot: TimeSlot,
  draft: NodeDraft,
  idFactory: IdFactory,
  options?: { majorId?: string | null },
): ItineraryEditor {
  const sourceDay = requireDay(editor, dayId);
  const settings = getItineraryProfessionalSettings(editor);
  const selected = options?.majorId ?? settings?.mainMajorId;
  if (sourceDay.parallelTracks?.tracks.length || (settings && selected !== settings.mainMajorId)) {
    const path = sourceDay.parallelTracks?.tracks.find((track) => track.majorId === selected)?.nodeIds ?? resolveDayEndpointNodes(sourceDay).map((node) => node.id);
    return insertProfessionalNode(editor, dayId, path.at(-1)!, path.length === 1 ? "after" : "before", draft, timeSlot, idFactory, selected);
  }
  const next = structuredClone(editor);
  const day = requireDay(next, dayId);
  const id = idFactory();
  day.nodes.push(createNode(id, timeSlot, draft));
  const index = day.nodes.filter((node) => node.timeSlot === timeSlot).length - 1;
  const moved = movePoint(next, id, { dayId, timeSlot, index }, idFactory);
  return moved;
}

/** 在目标节点前插入节点。 */
export function insertNodeBefore(
  editor: ItineraryEditor,
  targetNodeId: string,
  draft: NodeDraft,
  idFactory: IdFactory,
  options?: { majorId?: string | null },
): ItineraryEditor {
  const target = requireNodeLocation(editor, targetNodeId);
  const settings = getItineraryProfessionalSettings(editor);
  if (requireDay(editor, target.dayId).parallelTracks?.tracks.length || (settings && options?.majorId && options.majorId !== settings.mainMajorId)) {
    return insertProfessionalNode(editor, target.dayId, targetNodeId, "before", draft, draft.timeSlot ?? target.timeSlot, idFactory, options?.majorId);
  }
  return insertNodeAt(editor, target, draft, idFactory);
}

/** 在目标节点后插入节点。 */
export function insertNodeAfter(
  editor: ItineraryEditor,
  targetNodeId: string,
  draft: NodeDraft,
  idFactory: IdFactory,
  options?: { majorId?: string | null },
): ItineraryEditor {
  const target = requireNodeLocation(editor, targetNodeId);
  const settings = getItineraryProfessionalSettings(editor);
  if (requireDay(editor, target.dayId).parallelTracks?.tracks.length || (settings && options?.majorId && options.majorId !== settings.mainMajorId)) {
    return insertProfessionalNode(editor, target.dayId, targetNodeId, "after", draft, draft.timeSlot ?? target.timeSlot, idFactory, options?.majorId);
  }
  return insertNodeAt(editor, { ...target, index: target.index + 1 }, draft, idFactory);
}

/** 在目标节点前按“起点 → 终点”一次插入大区域火车的两个节点。 */
export function insertRailwayPairBefore(
  editor: ItineraryEditor,
  targetNodeId: string,
  pair: RailwayPairDraft,
  idFactory: IdFactory,
  options?: { majorId?: string | null },
): ItineraryEditor {
  return insertRailwayPair(editor, targetNodeId, pair, "before", idFactory, options);
}

/** 在目标节点后按“起点 → 终点”一次插入大区域火车的两个节点。 */
export function insertRailwayPairAfter(
  editor: ItineraryEditor,
  targetNodeId: string,
  pair: RailwayPairDraft,
  idFactory: IdFactory,
  options?: { majorId?: string | null },
): ItineraryEditor {
  return insertRailwayPair(editor, targetNodeId, pair, "after", idFactory, options);
}

/** 按普通图或专业公共轨道规则插入一段连续的铁路起终点。 */
function insertRailwayPair(
  editor: ItineraryEditor,
  targetNodeId: string,
  pair: RailwayPairDraft,
  placement: "before" | "after",
  idFactory: IdFactory,
  options?: { majorId?: string | null },
): ItineraryEditor {
  validateRailwayPairDraft(pair);
  // 从目标向外插入，保证专业公共首尾的两个节点都沿用相同的共享路径规则。
  const first = placement === "before"
    ? insertNodeBefore(editor, targetNodeId, pair.destination, idFactory, options)
    : insertNodeAfter(editor, targetNodeId, pair.origin, idFactory, options);
  const firstNodeId = findInsertedNodeId(editor, first);
  const next = placement === "before"
    ? insertNodeBefore(first, firstNodeId, pair.origin, idFactory, options)
    : insertNodeAfter(first, firstNodeId, pair.destination, idFactory, options);
  const secondNodeId = findInsertedNodeId(first, next);
  const originNodeId = placement === "before" ? secondNodeId : firstNodeId;
  const destinationNodeId = placement === "before" ? firstNodeId : secondNodeId;
  const rail = next.segments.find((segment) => (
    segment.fromNodeId === originNodeId && segment.toNodeId === destinationNodeId
  ));
  if (rail) {
    rail.sourcePayload = {
      version: 1,
      selected: null,
      backups: [],
      toolResultSnapshot: null,
      railwayInsertionPlacement: placement,
    };
  }
  return next;
}

/** 校验铁路起终点均为有主数据区域且不能指向同一区域。 */
function validateRailwayPairDraft(pair: RailwayPairDraft): void {
  const origin = pair.origin;
  const destination = pair.destination;
  if (origin.nodeType !== "railway_station" || destination.nodeType !== "railway_station") {
    throw new Error("火车起点和终点必须是大区域火车节点");
  }
  if (!origin.pointSpuId?.trim() || !destination.pointSpuId?.trim()) {
    throw new Error("请选择火车起点和终点大区域");
  }
  const originRegion = origin.railwayRegionName?.trim();
  const destinationRegion = destination.railwayRegionName?.trim();
  if (!originRegion || !destinationRegion) {
    throw new Error("火车起点和终点必须带有大区域信息");
  }
  if (normalizeEndpointTitle(originRegion) === normalizeEndpointTitle(destinationRegion)) {
    throw new Error("火车起点和终点不能选择同一个大区域");
  }
}

/** 从两次编辑快照中找出刚刚创建的节点 ID。 */
function findInsertedNodeId(before: ItineraryEditor, after: ItineraryEditor): string {
  const beforeIds = new Set(before.days.flatMap((day) => day.nodes.map((node) => node.id)));
  const inserted = after.days
    .flatMap((day) => day.nodes)
    .find((node) => !beforeIds.has(node.id));
  if (!inserted) throw new Error("火车节点插入失败");
  return inserted.id;
}

/** 删除节点及所有失效路线段。 */
export function deleteNode(editor: ItineraryEditor, nodeId: string, idFactory: IdFactory): ItineraryEditor {
  const target = requireNodeLocation(editor, nodeId);
  if (requireDay(editor, target.dayId).parallelTracks?.tracks.length) return deleteProfessionalNode(editor, target.dayId, nodeId, idFactory);
  const next = structuredClone(editor);
  let removed = false;
  for (const day of next.days) {
    const before = day.nodes.length;
    day.nodes = day.nodes.filter((node) => node.id !== nodeId);
    removed ||= day.nodes.length !== before;
  }
  if (!removed) throw new Error(`找不到节点：${nodeId}`);
  return withRebuiltGraph(next, idFactory);
}

/** 更换节点资源并同步同一次跨夜到达的副本，保留拓扑并重置受影响交通。 */
export function updateNode(editor: ItineraryEditor, nodeId: string, draft: NodeDraft): ItineraryEditor {
  const next = structuredClone(editor);
  const nodes = new Map(next.days.flatMap((day) => day.nodes.map((node) => [node.id, node] as const)));
  const node = nodes.get(nodeId);
  if (!node) throw new Error(`找不到节点：${nodeId}`);
  const endpointChanged = ["nodeType", "pointSpuId", "stayAreaId", "organizationSiteId", "title"]
    .some((key) => node[key as keyof ItineraryNode] !== draft[key as keyof NodeDraft]);
  const changedIds = endpointChanged ? overnightArrivalNodeIds(editor, node) : new Set([nodeId]);
  Object.assign(node, { ...draft, timeSlot: node.timeSlot });
  if (!endpointChanged) return next;
  for (const id of changedIds) {
    const copy = nodes.get(id);
    if (!copy || id === nodeId) continue;
    Object.assign(copy, {
      nodeType: node.nodeType, pointSpuId: node.pointSpuId, stayAreaId: node.stayAreaId,
      organizationSiteId: node.organizationSiteId, title: node.title,
      railwayRegionName: node.railwayRegionName ?? null, imageUrl: node.imageUrl ?? null,
    });
  }
  next.segments = next.segments.map((segment) => {
    if (!changedIds.has(segment.fromNodeId) && !changedIds.has(segment.toNodeId)) return segment;
    return {
      id: segment.id, startDayId: segment.startDayId, endDayId: segment.endDayId,
      routeSeq: segment.routeSeq, majorId: segment.majorId ?? null,
      fromNodeId: segment.fromNodeId, toNodeId: segment.toNodeId,
      ...defaultAdjacentTransport(nodes.get(segment.fromNodeId), nodes.get(segment.toNodeId), segment.startDayId === segment.endDayId),
    };
  });
  return next;
}

/** 只用空事实衔接、同一资源与真实到达日识别旧骨架的跨夜到达副本。 */
function overnightArrivalNodeIds(editor: ItineraryEditor, node: ItineraryNode): Set<string> {
  const ids = new Set([node.id]);
  if (!node.pointSpuId || !["railway_station", "airport"].includes(node.nodeType)) return ids;
  const locations = new Map(editor.days.flatMap((day) => day.nodes.map((item) => [item.id, { day, node: item }] as const)));
  const pairs: Array<[string, string]> = [];
  for (const bridge of editor.segments) {
    if ((bridge.fromNodeId !== node.id && bridge.toNodeId !== node.id)
      || bridge.startDayId === bridge.endDayId || bridge.sourceType !== "default_walk"
      || bridge.transportMode !== "walk" || bridge.routeType !== "local_transfer"
      || hasPersistedTransportFacts({ ...bridge, remark: bridge.remark === "默认步行，可在预览后按需调整" ? null : bridge.remark })) continue;
    const from = locations.get(bridge.fromNodeId);
    const to = locations.get(bridge.toNodeId);
    if (!from || !to || from.day.id !== bridge.startDayId || to.day.id !== bridge.endDayId
      || from.node.nodeType !== node.nodeType || to.node.nodeType !== node.nodeType
      || from.node.pointSpuId !== node.pointSpuId || to.node.pointSpuId !== node.pointSpuId
      || !to.day.dayDate) continue;
    const arrivals = editor.segments.filter((segment) => segment.toNodeId === from.node.id
      && segment.startDayId === from.day.id && segment.endDayId === from.day.id
      && segment.routeType === "main_transport"
      && segment.transportMode === (node.nodeType === "railway_station" ? "rail" : "air")
      && segment.departureAt && segment.arrivalAt
      && segment.arrivalAt.slice(0, 10) > segment.departureAt.slice(0, 10)
      && segment.arrivalAt.slice(0, 10) === to.day.dayDate);
    if (arrivals.length !== 1) continue;
    pairs.push([from.node.id, to.node.id]);
  }
  if (pairs.length === 1) pairs[0].forEach((id) => ids.add(id));
  return ids;
}

/** 同一入口处理同槽、跨槽和跨日移动。 */
export function movePoint(
  editor: ItineraryEditor,
  nodeId: string,
  target: MovePointTarget,
  idFactory: IdFactory,
  options?: { majorId?: string | null },
): ItineraryEditor {
  const source = requireNodeLocation(editor, nodeId);
  const next = structuredClone(editor);
  const previousTimeSlot = source.timeSlot;
  // 同日时段仅是节点归属；实际换日或连接变化仍沿用原有交通失效规则。
  if (source.dayId !== target.dayId && previousTimeSlot !== target.timeSlot) {
    next.segments = next.segments.map((segment) => {
      if (segment.transportMode !== "rail"
        || (!hasPersistedTransportFacts(segment)
          && !segment.automaticQueryDepartureSlot)
        || segment.fromNodeId !== nodeId) return segment;
      return {
        ...segment,
        routeCode: null,
        departureAt: null,
        arrivalAt: null,
        distanceKm: null,
        durationMinutes: null,
        sourceType: "pending",
        sourcePayload: null,
        automaticQueryDepartureSlot: target.timeSlot,
      };
    });
  }
  if (requireDay(editor, source.dayId).parallelTracks?.tracks.length || requireDay(editor, target.dayId).parallelTracks?.tracks.length) {
    const moved = moveProfessionalNode(next, source.dayId, nodeId, target, idFactory, options?.majorId);
    return source.dayId === target.dayId && previousTimeSlot !== target.timeSlot
      ? preserveRailwayTransferAfterSlotMove(editor, moved, nodeId) : moved;
  }
  const stationPartners = new Set(
    editor.segments
      .filter((segment) => (segment.transportMode === "rail" || segment.transportMode === "air")
        && segment.fromNodeId === nodeId)
      .map((segment) => segment.toNodeId),
  );
  const stationPredecessors = new Set(
    editor.segments
      .filter((segment) => (segment.transportMode === "rail" || segment.transportMode === "air")
        && segment.toNodeId === nodeId)
      .map((segment) => segment.fromNodeId),
  );
  let moved: ItineraryNode | undefined;
  for (const day of next.days) {
    const index = day.nodes.findIndex((node) => node.id === nodeId);
    if (index >= 0) moved = day.nodes.splice(index, 1)[0];
  }
  if (!moved) throw new Error(`找不到节点：${nodeId}`);
  const day = requireDay(next, target.dayId);
  moved.timeSlot = target.timeSlot;
  const slotNodes = day.nodes.filter((node) => node.timeSlot === target.timeSlot);
  const beforeId = slotNodes[Math.max(0, target.index)]?.id;
  const insertAt = beforeId ? day.nodes.findIndex((node) => node.id === beforeId) : day.nodes.length;
  day.nodes.splice(insertAt, 0, moved);
  if (source.dayId === target.dayId && previousTimeSlot === target.timeSlot) {
    // 同槽排序必须命中实际落点，不能依赖重建将拆开的站对悄悄移回别处。
    const originalSlotNodes = requireDay(editor, source.dayId).nodes
      .filter((node) => node.timeSlot === previousTimeSlot)
      .sort((left, right) => left.sortOrder - right.sortOrder);
    const originalIndex = originalSlotNodes.findIndex((node) => node.id === nodeId);
    const unchangedPosition = originalSlotNodes[originalIndex + 1]?.id === beforeId;
    const slotNodeIds = new Set(originalSlotNodes.map((node) => node.id));
    const stationPairs = editor.segments.filter((segment) => (segment.transportMode === "rail" || segment.transportMode === "air")
      && segment.startDayId === day.id && segment.endDayId === day.id
      && slotNodeIds.has(segment.fromNodeId) && slotNodeIds.has(segment.toNodeId));
    const movingIds = new Set([nodeId]);
    // 共享中转站的多段交通是一条连续站链，移动首端时携带所有后续站点。
    for (let count = 0; count < stationPairs.length; count += 1) {
      for (const pair of stationPairs) if (movingIds.has(pair.fromNodeId)) movingIds.add(pair.toNodeId);
    }
    if (!unchangedPosition && (stationPairs.some((pair) => pair.toNodeId === nodeId)
      || (beforeId && (movingIds.has(beforeId)
        || stationPairs.some((pair) => pair.toNodeId === beforeId && !movingIds.has(pair.fromNodeId)))))) {
      throw new Error("铁路或航班站对必须保持连续，请选择整段交通之外的位置。");
    }
    if (!unchangedPosition && movingIds.size > 1) {
      const movingNodes = originalSlotNodes.filter((node) => movingIds.has(node.id));
      day.nodes = day.nodes.filter((node) => !movingIds.has(node.id));
      const position = beforeId ? day.nodes.findIndex((node) => node.id === beforeId) : day.nodes.length;
      day.nodes.splice(position, 0, ...movingNodes);
    }
  }
  if (previousTimeSlot === target.timeSlot && stationPartners.size) {
    const sameSlotPartners = day.nodes.filter((node) => (
      stationPartners.has(node.id) && node.timeSlot === target.timeSlot
    ));
    if (sameSlotPartners.length) {
      day.nodes = day.nodes.filter((node) => !sameSlotPartners.includes(node));
      const movedIndex = day.nodes.findIndex((node) => node.id === moved.id);
      day.nodes.splice(movedIndex + 1, 0, ...sameSlotPartners);
    }
  }
  if (previousTimeSlot === target.timeSlot && stationPredecessors.size) {
    const predecessor = day.nodes.find((node) => stationPredecessors.has(node.id)
      && node.timeSlot === target.timeSlot);
    const movedIndex = day.nodes.findIndex((node) => node.id === moved.id);
    if (predecessor && movedIndex >= 0) {
      day.nodes.splice(movedIndex, 1);
      const predecessorIndex = day.nodes.findIndex((node) => node.id === predecessor.id);
      day.nodes.splice(predecessorIndex + 1, 0, moved);
    }
  }
  const rebuilt = withRebuiltGraph(next, idFactory, { preserveStationPairs: true });
  const previousPositionIssues = new Set(validateEditor(editor)
    .filter((issue) => issue.code === "intermediate-node-must-be-point").map((issue) => issue.subjectKey));
  if (validateEditor(rebuilt).some((issue) => issue.code === "intermediate-node-must-be-point"
    && !previousPositionIssues.has(issue.subjectKey))) {
    throw new Error("学校校区只能放在当天首尾，请调整插入位置。");
  }
  return source.dayId === target.dayId && previousTimeSlot !== target.timeSlot
    ? preserveRailwayTransferAfterSlotMove(editor, rebuilt, nodeId) : rebuilt;
}

/** 站对是连续交通路径；显示时段变化不能把考察点插进途中或反转路线。 */
function orderedNodesWithStationPairs(day: ItineraryDay, segments: ItinerarySegment[]): ItineraryNode[] {
  const ordered = orderedNodes(day);
  const nodes = new Map(ordered.map((node) => [node.id, node]));
  const nextStation = new Map<string, string>();
  for (const segment of segments) {
    if (segment.startDayId !== day.id || segment.endDayId !== day.id || segment.majorId) continue;
    const stationType = segment.transportMode === "rail" ? "railway_station"
      : segment.transportMode === "air" ? "airport" : null;
    if (stationType && nodes.get(segment.fromNodeId)?.nodeType === stationType
      && nodes.get(segment.toNodeId)?.nodeType === stationType) {
      nextStation.set(segment.fromNodeId, segment.toNodeId);
    }
  }
  const arrivals = new Set(nextStation.values());
  const result: ItineraryNode[] = [];
  const visited = new Set<string>();
  for (const start of [...ordered.filter((node) => !arrivals.has(node.id)), ...ordered]) {
    let node: ItineraryNode | undefined = start;
    while (node && !visited.has(node.id)) {
      visited.add(node.id);
      result.push(node);
      node = nodes.get(nextStation.get(node.id) ?? "");
    }
  }
  return result;
}

/** 按需要交通的相邻端点返回默认方式；跨天住宿衔接由重建入口排除。 */
export function defaultAdjacentTransport(
  fromNode: ItineraryNode | undefined,
  toNode: ItineraryNode | undefined,
  sameDay: boolean,
): Pick<ItinerarySegment, "routeType" | "transportMode" | "sourceType"> {
  const fromPointSpuId = fromNode?.pointSpuId?.trim();
  const toPointSpuId = toNode?.pointSpuId?.trim();
  const sameStructuredEndpoint = Boolean(
    fromPointSpuId
    && toPointSpuId
    && fromPointSpuId === toPointSpuId,
  );
  if (
    fromNode?.nodeType === "railway_station"
    && toNode?.nodeType === "railway_station"
    && (sameDay || !sameStructuredEndpoint)
  ) {
    return { routeType: "main_transport", transportMode: "rail", sourceType: "pending" };
  }
  if (
    fromNode?.nodeType === "airport"
    && toNode?.nodeType === "airport"
    && (sameDay || !sameStructuredEndpoint)
  ) {
    return { routeType: "main_transport", transportMode: "air", sourceType: "pending" };
  }
  if (!sameDay) {
    return { routeType: "local_transfer", transportMode: "walk", sourceType: "default_walk" };
  }
  return { routeType: "charter_bus", transportMode: "bus", sourceType: "pending" };
}

/** 未带轨道容器的专业路线仍有明确端点，不能退回全日节点数组推导路径。 */
export function hasExplicitProfessionalRoutes(day: ItineraryDay, segments: ItinerarySegment[]): boolean {
  return !day.parallelTracks?.tracks.length && segments.some((segment) => (
    segment.startDayId === day.id && segment.endDayId === day.id && Boolean(segment.majorId)
  ));
}

/** 专业归属缺失但真实端点仍分叉或汇合时，保留路线事实供人工修复，不猜测专业轨道。 */
function hasUnassignedBranchingRoutes(day: ItineraryDay, segments: ItinerarySegment[]): boolean {
  if (day.parallelTracks?.tracks.length) return false;
  const nodes = new Set(day.nodes.map((node) => node.id));
  const outgoing = new Map<string, Set<string>>();
  const incoming = new Map<string, Set<string>>();
  for (const segment of segments) {
    if (segment.majorId || segment.startDayId !== day.id || segment.endDayId !== day.id
      || !nodes.has(segment.fromNodeId) || !nodes.has(segment.toNodeId)) continue;
    const targets = outgoing.get(segment.fromNodeId) ?? new Set<string>();
    const sources = incoming.get(segment.toNodeId) ?? new Set<string>();
    targets.add(segment.toNodeId);
    sources.add(segment.fromNodeId);
    // 不同端点的分叉或汇合才证明不是普通线性日；同端点重复记录不改变重建规则。
    if (targets.size > 1 || sources.size > 1) return true;
    outgoing.set(segment.fromNodeId, targets);
    incoming.set(segment.toNodeId, sources);
  }
  return false;
}

/** 按既存专业段的真实端点定位其在轨道中的相邻位置。 */
function professionalSegmentPairIndex(
  dayId: string,
  track: ItineraryParallelTrack,
  segment: ItinerarySegment | undefined,
): number {
  if (
    !segment
    || segment.majorId !== track.majorId
    || segment.startDayId !== dayId
    || segment.endDayId !== dayId
  ) return -1;
  return track.nodeIds.findIndex((fromNodeId, index) => (
    fromNodeId === segment.fromNodeId && track.nodeIds[index + 1] === segment.toNodeId
  ));
}

/** 沿已声明的业务路径补齐相邻交通；显式分叉路线不从显示顺序重新连线。 */
export function rebuildAdjacentSegments(
  days: ItineraryDay[],
  segments: ItinerarySegment[],
  idFactory: IdFactory,
  options?: { preserveStationPairs?: boolean },
): ItinerarySegment[] {
  segments = normalizeRailwayOvernightStays({ days, segments }).segments;
  const originalNodeDays = new Map(days.flatMap((day) => day.nodes.map((node) => [node.id, day.id])));
  const overnightStays = railwayOvernightStayIds(segments);
  days = days.map((day) => ({ ...day, nodes: day.nodes.filter((node) => !overnightStays.has(node.id)),
    parallelTracks: day.parallelTracks ? { ...day.parallelTracks, tracks: day.parallelTracks.tracks.map((track) => ({
      ...track, nodeIds: track.nodeIds.filter((id) => !overnightStays.has(id)),
    })) } : day.parallelTracks,
  }));
  const hasTransportFacts = (segment?: ItinerarySegment) => Boolean(
    segment?.sourcePayload?.selected
    || segment?.sourcePayload?.backups?.length
    || segment?.sourcePayload?.toolResultSnapshot
    || segment?.sourcePayload?.railwayTransfer
    || segment?.routeCode
    || segment?.transportSpuId
    || segment?.transportSkuId
    || segment?.busSpuId
    || segment?.busSkuId
    || segment?.departureAt
    || segment?.arrivalAt
    || segment?.distanceKm
    || (segment?.durationMinutes !== null && segment?.durationMinutes !== undefined)
    || segment?.segmentDescription?.trim()
    || segment?.remark?.trim()
    || segment?.automaticQueryDepartureSlot
  );
  const isEmptyPlaceholder = (segment?: ItinerarySegment) => Boolean(
    segment
    && !hasTransportFacts(segment)
    && (
      isPendingTransportSource(segment.sourceType)
      || segment.sourceType === "default_walk"
      || !segment.sourceType
    )
  );
  const orderedDays = [...days].sort((left, right) => left.dayNumber - right.dayNumber);
  const explicitDayIds = new Set(orderedDays.filter((day) => (
    hasExplicitProfessionalRoutes(day, segments) || hasUnassignedBranchingRoutes(day, segments)
  )).map((day) => day.id));
  const explicitSegments = segments.filter((segment) => (
    explicitDayIds.has(segment.startDayId) && segment.startDayId === segment.endDayId
  ));
  const explicitSegmentIds = new Set(explicitSegments.map((segment) => segment.id));
  const nodesById = new Map(
    orderedDays.flatMap((day) => day.nodes.map((item) => [item.id, item] as const)),
  );
  const baseExisting = new Map(
    segments
      .filter((segment) => !segment.majorId)
      .map((segment) => [`${segment.fromNodeId}:${segment.toNodeId}`, segment]),
  );
  const pairs: Array<{ fromDayId: string; toDayId: string; fromNodeId: string; toNodeId: string }> = [];
  let previous: { dayId: string; dayNumber: number; nodeId: string } | null = null;
  for (const day of orderedDays) {
    const track = day.parallelTracks?.tracks[0];
    const explicit = explicitDayIds.has(day.id);
    const ordered = track || explicit || options?.preserveStationPairs === false
      ? orderedNodes(day)
      : orderedNodesWithStationPairs(day, segments);
    if (!ordered.length) continue;
    const daySegments = explicit ? explicitSegments.filter((segment) => segment.startDayId === day.id) : [];
    const starts = explicit ? ordered.filter((node) => !daySegments.some((segment) => segment.toNodeId === node.id)) : [];
    const ends = explicit ? ordered.filter((node) => !daySegments.some((segment) => segment.fromNodeId === node.id)) : [];
    const previousNodeType = previous ? nodesById.get(previous.nodeId)?.nodeType : undefined;
    const arrivalNode = previous
      && day.dayNumber > previous.dayNumber + 1
      && !track
      && (previousNodeType === "railway_station" || previousNodeType === "airport")
      ? ordered.find((node) => node.nodeType === previousNodeType)
      : undefined;
    const entryId = (starts.length === 1 ? starts[0].id : undefined) ?? arrivalNode?.id ?? track?.nodeIds[0] ?? ordered[0].id;
    const exitId = (ends.length === 1 ? ends[0].id : undefined) ?? track?.nodeIds.at(-1) ?? ordered.at(-1)!.id;
    if (previous) {
      const stayContinuation = previousNodeType === "stay_area" && nodesById.get(entryId)?.nodeType === "stay_area";
      const existing = baseExisting.get(`${previous.nodeId}:${entryId}`);
      // 跨天住宿是日程衔接，临时不一致也不推断交通；已填写的交通事实继续保留。
      const explicitCrossDay = segments.some((segment) => segment.majorId && segment.startDayId === previous!.dayId && segment.endDayId === day.id);
      if ((!stayContinuation || hasTransportFacts(existing)) && !explicitCrossDay) {
        pairs.push({ fromDayId: previous.dayId, toDayId: day.id, fromNodeId: previous.nodeId, toNodeId: entryId });
      }
    }
    if (!track && !explicit) {
      for (let index = 0; index < ordered.length - 1; index += 1) {
        pairs.push({ fromDayId: day.id, toDayId: day.id, fromNodeId: ordered[index].id, toNodeId: ordered[index + 1].id });
      }
    }
    previous = { dayId: day.id, dayNumber: day.dayNumber, nodeId: exitId };
  }
  const reservedSequences = new Set(explicitSegments.filter((segment) => !segment.majorId).map((segment) => segment.routeSeq));
  let baseSequence = 0;
  const base = pairs.map((pair) => {
    do { baseSequence += 1; } while (reservedSequences.has(baseSequence));
    const preserved = baseExisting.get(`${pair.fromNodeId}:${pair.toNodeId}`);
    if (
      preserved
      && preserved.startDayId === pair.fromDayId
      && preserved.endDayId === pair.toDayId
      && !isEmptyPlaceholder(preserved)
    ) {
      return {
        ...preserved,
        routeSeq: baseSequence,
      };
    }
    return {
      id: preserved?.id ?? idFactory(),
      startDayId: pair.fromDayId,
      endDayId: pair.toDayId,
      routeSeq: baseSequence,
      fromNodeId: pair.fromNodeId,
      toNodeId: pair.toNodeId,
      ...defaultAdjacentTransport(
        nodesById.get(pair.fromNodeId),
        nodesById.get(pair.toNodeId),
        pair.fromDayId === pair.toDayId,
      ),
    };
  });
  const majorExistingById = new Map(
    segments
      .filter((segment) => Boolean(segment.majorId))
      .map((segment) => [segment.id, segment]),
  );
  const referencedMajorIds = new Set<string>();
  const trackSegmentIds = new Set(orderedDays.flatMap((day) => day.parallelTracks?.tracks.flatMap((track) => track.segmentIds) ?? []));
  const reservedMajorSequences = new Map<string, Set<number>>();
  segments.forEach((segment) => {
    if (!segment.majorId || trackSegmentIds.has(segment.id)) return;
    const reserved = reservedMajorSequences.get(segment.majorId) ?? new Set<number>();
    reserved.add(segment.routeSeq);
    reservedMajorSequences.set(segment.majorId, reserved);
  });
  const majorSequences = new Map<string, number>();
  const professional = orderedDays.flatMap((day) => (day.parallelTracks?.tracks ?? []).flatMap((track) => (
    track.segmentIds.flatMap((segmentId, index) => {
      const preserved = majorExistingById.get(segmentId);
      const existingPairIndex = professionalSegmentPairIndex(day.id, track, preserved);
      // 完整引用链仍兼容历史缺记录；部分引用只能按真实端点落位，避免缺口后的段整体前移。
      const pairIndex = existingPairIndex >= 0
        ? existingPairIndex
        : track.segmentIds.length === track.nodeIds.length - 1 ? index : -1;
      const fromNodeId = track.nodeIds[pairIndex];
      const toNodeId = track.nodeIds[pairIndex + 1];
      if (!fromNodeId || !toNodeId || referencedMajorIds.has(segmentId)) return [];
      referencedMajorIds.add(segmentId);
      let routeSeq = majorSequences.get(track.majorId) ?? 0;
      do { routeSeq += 1; } while (reservedMajorSequences.get(track.majorId)?.has(routeSeq));
      majorSequences.set(track.majorId, routeSeq);
      if (
        preserved
        && preserved.startDayId === day.id
        && preserved.endDayId === day.id
        && preserved.fromNodeId === fromNodeId
        && preserved.toNodeId === toNodeId
        && !isEmptyPlaceholder(preserved)
      ) {
        return [{ ...structuredClone(preserved), routeSeq, majorId: track.majorId }];
      }
      return [{
        id: segmentId,
        startDayId: day.id,
        endDayId: day.id,
        routeSeq,
        majorId: track.majorId,
        fromNodeId,
        toNodeId,
        ...defaultAdjacentTransport(
          nodesById.get(fromNodeId),
          nodesById.get(toNodeId),
          true,
        ),
      }];
    })
  )));
  const unreferencedMajor = segments
    .filter((segment) => Boolean(segment.majorId) && !referencedMajorIds.has(segment.id) && !explicitSegmentIds.has(segment.id))
    .map((segment) => structuredClone(segment));
  const rebuilt = [...base, ...professional, ...unreferencedMajor, ...structuredClone(explicitSegments)];
  // 逐日住宿未排齐不等于取消车次；仅真实端点被删除或换日时沿用原失效规则。
  for (const segment of segments) {
    if (!segment.sourcePayload?.railwayOvernight || segment.transportMode !== "rail"
      || originalNodeDays.get(segment.fromNodeId) !== segment.startDayId
      || originalNodeDays.get(segment.toNodeId) !== segment.endDayId
      || rebuilt.some((item) => item.id === segment.id)) continue;
    const routeSeq = Math.max(0, ...rebuilt.filter((item) => (item.majorId ?? null) === (segment.majorId ?? null)).map((item) => item.routeSeq)) + 1;
    rebuilt.push({ ...structuredClone(segment), routeSeq });
  }
  return rebuilt;
}

/** 将历史火车/航班的非法端点转换为不携带旧事实的普通步行。 */
export function repairInvalidTransportEndpointSegments(
  editor: ItineraryEditor,
  idFactory: IdFactory,
  baseline?: ItineraryEditor,
): ItineraryEditor {
  const next = structuredClone(editor);
  const nodesById = new Map(
    next.days.flatMap((day) => day.nodes.map((node) => [node.id, node] as const)),
  );
  const endpointSignature = (segment: ItinerarySegment) => [
    segment.id,
    segment.transportMode,
    segment.fromNodeId,
    segment.toNodeId,
  ].join(":");
  const invalidEndpointSignatures = (source?: ItineraryEditor) => {
    if (!source) return new Set<string>();
    const sourceNodes = new Map(
      source.days.flatMap((day) => day.nodes.map((node) => [node.id, node] as const)),
    );
    return new Set(source.segments.filter((segment) => (
      segment.transportMode === "rail"
      && (
        sourceNodes.get(segment.fromNodeId)?.nodeType !== "railway_station"
        || sourceNodes.get(segment.toNodeId)?.nodeType !== "railway_station"
      )
    ) || (
      segment.transportMode === "air"
      && (
        sourceNodes.get(segment.fromNodeId)?.nodeType !== "airport"
        || sourceNodes.get(segment.toNodeId)?.nodeType !== "airport"
      )
    )).map(endpointSignature));
  };
  const allowedHistoricalMismatches = invalidEndpointSignatures(baseline);
  let repairedAny = false;
  next.segments = next.segments.map((segment) => {
    const fromNode = nodesById.get(segment.fromNodeId);
    const toNode = nodesById.get(segment.toNodeId);
    const endpointKindInvalid = (
      segment.transportMode === "rail"
      && (fromNode?.nodeType !== "railway_station" || toNode?.nodeType !== "railway_station")
    ) || (
      segment.transportMode === "air"
      && (fromNode?.nodeType !== "airport" || toNode?.nodeType !== "airport")
    );
    if (!endpointKindInvalid || allowedHistoricalMismatches.has(endpointSignature(segment))) {
      return segment;
    }
    repairedAny = true;
    return {
      id: segment.id || idFactory(),
      startDayId: segment.startDayId,
      endDayId: segment.endDayId,
      routeSeq: segment.routeSeq,
      majorId: segment.majorId,
      routeType: "local_transfer",
      transportMode: "walk",
      fromNodeId: segment.fromNodeId,
      toNodeId: segment.toNodeId,
      sourceType: "default_walk",
    };
  });
  return repairedAny ? withRebuiltGraph(next, idFactory) : next;
}

/** 设置主选项，并从备选中剔除同一来源选项。 */
export function setSelectedTransport(
  payload: ItinerarySourcePayload,
  selected: TransportSelection | null,
): ItinerarySourcePayload {
  const clonedPayload = structuredClone(payload);
  const selectedKey = selected ? selectionKey(selected) : null;
  return {
    ...clonedPayload,
    selected: selected ? structuredClone(selected) : null,
    backups: clonedPayload.backups.filter((item) => selectionKey(item) !== selectedKey),
  };
}

/** 设置备选项，并保证主备互斥与备选唯一。 */
export function setBackupTransports(
  payload: ItinerarySourcePayload,
  backups: TransportSelection[],
): ItinerarySourcePayload {
  const selectedKey = payload.selected ? selectionKey(payload.selected) : null;
  const unique = new Map(backups.map((item) => [selectionKey(item), structuredClone(item)]));
  if (selectedKey) unique.delete(selectedKey);
  return { ...structuredClone(payload), backups: [...unique.values()] };
}

/** 每日日期和天次已完整连续时，仅归一由实际安排派生的结束日期。 */
export function normalizeEditorEndDate(editor: ItineraryEditor): ItineraryEditor {
  const { startDate, endDate, days } = editor;
  if (!startDate || !endDate || startDate > endDate
    || dateByOffset(startDate, 0) !== startDate || dateByOffset(endDate, 0) !== endDate
    || days.length < ITINERARY_MIN_DAYS || days.length > ITINERARY_MAX_DAYS
    || days.some((day, index) => day.dayNumber !== index + 1 || day.dayDate !== dateByOffset(startDate, index))) return editor;
  const expectedEnd = dateByOffset(startDate, days.length - 1);
  return endDate === expectedEnd ? editor : { ...editor, endDate: expectedEnd };
}

/** 对齐后端日期约束，只检查当前草稿，不修正日期或业务天数。 */
export function validateEditorDates(editor: ItineraryEditor): ItineraryValidationIssue[] {
  const start = editor.startDate || null;
  const end = editor.endDate || null;
  if ((start === null) !== (end === null)) {
    return [{ code: "itinerary-date-header-incomplete", detail: start
      ? `行程开始日期为 ${start}，但缺少结束日期；开始日期和结束日期需要成对存在。`
      : `行程结束日期为 ${end}，但缺少开始日期；开始日期和结束日期需要成对存在。` }];
  }
  if (!start || !end) {
    return editor.days.filter((day) => Boolean(day.dayDate)).map((day) => ({
      code: "day-date-without-itinerary-date",
      detail: `行程未设置起止日期，但第 ${day.dayNumber} 天的每日日期为 ${day.dayDate}。`,
    }));
  }
  if (dateByOffset(start, 0) !== start || dateByOffset(end, 0) !== end) {
    return [{ code: "itinerary-date-format-invalid", detail: `行程开始日期（${start}）或结束日期（${end}）不是有效日期。` }];
  }
  const rangeDays = dateDifference(start, end)! + 1;
  if (rangeDays < 1) {
    return [{ code: "itinerary-date-range-invalid", detail: `行程结束日期 ${end} 早于开始日期 ${start}。` }];
  }
  if (rangeDays !== editor.days.length) {
    return [{ code: "itinerary-date-count-invalid", detail: `当前行程共 ${editor.days.length} 天，但起止日期为 ${start} 至 ${end}，共 ${rangeDays} 天。` }];
  }
  return editor.days.flatMap((day) => {
    const expected = dateByOffset(start, day.dayNumber - 1);
    return day.dayDate === expected ? [] : [{
      code: "itinerary-day-date-invalid",
      detail: `第 ${day.dayNumber} 天的每日日期${day.dayDate ? `为 ${day.dayDate}` : "为空"}，按开始日期 ${start} 应为 ${expected}。`,
    }];
  });
}

/** 执行不依赖服务端的图校验；保存前可额外要求节点关联结构化资源。 */
export function validateEditor(
  editor: ItineraryEditor,
  options: {
    requireNodeResources?: boolean;
    allowedSnapshotNodeIds?: ReadonlySet<string>;
  } = {},
): ItineraryValidationIssue[] {
  const issues: ItineraryValidationIssue[] = validateEditorDates(editor);
  if (editor.days.length < ITINERARY_MIN_DAYS || editor.days.length > ITINERARY_MAX_DAYS) {
    issues.push({ code: "day-count-invalid", detail: "行程天数必须在 1-30 天之间" });
  }
  const nodeIds = new Set<string>();
  editor.days.forEach((day, dayIndex) => {
    if (day.dayNumber !== dayIndex + 1) {
      issues.push({ code: "day-number-not-continuous", detail: `第 ${dayIndex + 1} 天序号不连续` });
    }
    for (const node of day.nodes) {
      if (!node.title.trim()) issues.push({ code: "node-title-required", detail: `节点 ${node.id} 缺少标题` });
      if (
        options.requireNodeResources
        && node.nodeType === "custom"
        && !options.allowedSnapshotNodeIds?.has(node.id)
      ) {
        issues.push({
          code: "node-resource-required",
          detail: `第 ${day.dayNumber} 天${validationTimeSlotLabels[node.timeSlot]}的“${node.title.trim() || "未命名节点"}”未关联结构化点位，请重新选择点位后再保存`,
          subjectKey: `${day.id}:node:${node.id}`,
        });
      }
      if (nodeIds.has(node.id)) issues.push({ code: "node-id-duplicate", detail: `节点 ${node.id} 重复` });
      nodeIds.add(node.id);
    }
    if (!day.parallelTracks?.tracks.length) {
      validateNodePositions(orderedNodes(day), issues, `${day.id}:linear`);
    }
  });
  const expectedSegments = rebuildAdjacentSegments(editor.days, editor.segments, () => "pending");
  const signature = (segment: ItinerarySegment) =>
    `${segment.majorId ?? "base"}:${segment.routeSeq}:${segment.fromNodeId}:${segment.toNodeId}`;
  if (
    expectedSegments.length !== editor.segments.length ||
    expectedSegments.map(signature).sort().join("|") !== editor.segments.map(signature).sort().join("|")
  ) {
    issues.push({ code: "segment-adjacency-invalid", detail: "路线段与当前节点顺序不一致" });
  }
  validateParallelTracks(editor, nodeIds, issues);
  return issues;
}

/** 将修复前页面生成的 local-* 实体标识转换为可持久化 UUID，并同步全部图引用。 */
export function normalizeTemporaryEntityIds(
  editor: ItineraryEditor,
  idFactory: IdFactory,
): {
  editor: ItineraryEditor;
  dayIdMap: Map<string, string>;
  segmentIdMap: Map<string, string>;
} {
  const replacementMap = (ids: string[]) => new Map(
    ids.filter((id) => id.startsWith("local-")).map((id) => [id, idFactory()]),
  );
  const dayIdMap = replacementMap(editor.days.map((day) => day.id));
  const nodeIdMap = replacementMap(editor.days.flatMap((day) => day.nodes.map((node) => node.id)));
  const segmentIdMap = replacementMap(editor.segments.map((segment) => segment.id));
  const trackIdMap = replacementMap(editor.days.flatMap((day) => (
    day.parallelTracks?.tracks.map((track) => track.id) ?? []
  )));
  const replace = (ids: Map<string, string>, id: string) => ids.get(id) ?? id;
  if (![dayIdMap, nodeIdMap, segmentIdMap, trackIdMap].some((ids) => ids.size)) {
    return { editor, dayIdMap, segmentIdMap };
  }
  return {
    editor: {
      ...editor,
      days: editor.days.map((day) => ({
        ...day,
        id: replace(dayIdMap, day.id),
        nodes: day.nodes.map((node) => ({ ...node, id: replace(nodeIdMap, node.id) })),
        parallelTracks: day.parallelTracks
          ? {
              ...day.parallelTracks,
              tracks: day.parallelTracks.tracks.map((track) => ({
                ...track,
                id: replace(trackIdMap, track.id),
                nodeIds: track.nodeIds.map((id) => replace(nodeIdMap, id)),
                segmentIds: track.segmentIds.map((id) => replace(segmentIdMap, id)),
              })),
            }
          : day.parallelTracks,
      })),
      segments: editor.segments.map((segment) => ({
        ...segment,
        id: replace(segmentIdMap, segment.id),
        startDayId: replace(dayIdMap, segment.startDayId),
        endDayId: replace(dayIdMap, segment.endDayId),
        fromNodeId: replace(nodeIdMap, segment.fromNodeId),
        toNodeId: replace(nodeIdMap, segment.toNodeId),
        sourcePayload: segment.sourcePayload?.railwayOvernight ? { ...segment.sourcePayload, railwayOvernight: {
          stayNodeIds: segment.sourcePayload.railwayOvernight.stayNodeIds.map((id) => replace(nodeIdMap, id)),
        } } : segment.sourcePayload,
      })),
    },
    dayIdMap,
    segmentIdMap,
  };
}

/** 统一重排行程图并清理并行轨道失效引用。 */
function withRebuiltGraph(
  editor: ItineraryEditor,
  idFactory: IdFactory,
  options?: { preserveStationPairs?: boolean },
): ItineraryEditor {
  for (const day of editor.days.filter((item) => hasExplicitProfessionalRoutes(item, editor.segments) || hasUnassignedBranchingRoutes(item, editor.segments))) {
    const nodes = new Set(day.nodes.map((node) => node.id));
    const routes = editor.segments.filter((segment) => segment.startDayId === day.id && segment.endDayId === day.id);
    if (routes.some((segment) => !nodes.has(segment.fromNodeId) || !nodes.has(segment.toNodeId))
      || day.nodes.some((node) => !routes.some((segment) => segment.fromNodeId === node.id || segment.toNodeId === node.id))) {
      throw new Error("专业路径尚未完整读取，请重新获取完整行程后再增删点位或跨日移动。");
    }
  }
  editor.days = editor.days.map((day) => ({ ...day, nodes: normalizeSortOrders(day.nodes) }));
  editor.segments = rebuildAdjacentSegments(editor.days, editor.segments, idFactory, options);
  return editor;
}

/** 判断路线是否携带会在端点时段改变后失效的交通事实。 */
function hasPersistedTransportFacts(segment?: ItinerarySegment): boolean {
  if (!segment) return false;
  return Boolean(
    segment.transportSpuId
    || segment.transportSkuId
    || segment.busSpuId
    || segment.busSkuId
    || segment.routeCode
    || segment.departureAt
    || segment.arrivalAt
    || segment.distanceKm
    || segment.durationMinutes !== null && segment.durationMinutes !== undefined
    || segment.segmentDescription?.trim()
    || segment.remark?.trim()
    || segment.sourcePayload?.selected
    || segment.sourcePayload?.backups.length
    || segment.sourcePayload?.toolResultSnapshot,
  );
}

/** 按时段重排节点序号。 */
function normalizeSortOrders(nodes: ItineraryNode[]): ItineraryNode[] {
  const result: ItineraryNode[] = [];
  for (const slot of ITINERARY_TIME_SLOTS) {
    nodes.filter((node) => node.timeSlot === slot).forEach((node, index) => result.push({ ...node, sortOrder: index + 1 }));
  }
  return result;
}

/** 按时段及槽内序号返回单日节点。 */
function orderedNodes(day: ItineraryDay): ItineraryNode[] {
  return ITINERARY_TIME_SLOTS.flatMap((slot) =>
    day.nodes
      .filter((node) => node.timeSlot === slot)
      .sort((left, right) => left.sortOrder - right.sortOrder),
  );
}

/** 住宿节点变更时同步当天最后一个住宿；无关编辑保留每日住宿的人工选择。 */
export function synchronizeDayStayAreas(previous: ItineraryEditor, next: ItineraryEditor): ItineraryEditor {
  const noStayId = [...next.days, ...previous.days].find((day) => day.stayAreaName?.trim() === "无" && day.stayAreaId)?.stayAreaId ?? null;
  const arrivalStays = new Set(next.segments.flatMap((segment) => segment.sourcePayload?.railwayOvernight?.stayNodeIds
    .filter((id) => next.days.some((day) => day.id !== segment.startDayId
      && day.nodes.some((node) => node.id === id && node.timeSlot === "morning"))) ?? []));
  const previousDays = new Map(previous.days.map((day) => [day.id, day]));
  const stays = (day?: ItineraryDay) => day
    ? orderedNodes(day).filter((node) => node.nodeType === "stay_area" && node.stayAreaId && !arrivalStays.has(node.id))
    : [];
  const identity = (nodes: ItineraryNode[]) => JSON.stringify(nodes.map((node) => [node.id, node.stayAreaId, node.title]));
  return { ...next, days: next.days.map((day) => {
    const oldStays = stays(previousDays.get(day.id));
    const nextStays = stays(day);
    const defaultNoStay = { ...day, stayAreaId: noStayId, stayAreaName: "无" };
    if (identity(oldStays) === identity(nextStays)) {
      return !nextStays.length && !day.stayAreaId && (!day.stayAreaName?.trim() || day.stayAreaName.trim() === "无")
        ? defaultNoStay : day;
    }
    const last = nextStays.at(-1);
    if (last) return { ...day, stayAreaId: last.stayAreaId, stayAreaName: last.title.trim() };
    // 只清理曾跟随节点的住宿；“火车上”等独立人工设置不属于已删除节点。
    return oldStays.some((node) => node.stayAreaId === day.stayAreaId)
      || !day.stayAreaId && !day.stayAreaName?.trim() ? defaultNoStay : day;
  }) };
}

/** 从草稿创建最小节点。 */
function createNode(id: string, timeSlot: TimeSlot, draft: NodeDraft): ItineraryNode {
  return { id, timeSlot, sortOrder: 1, nodeType: "custom", ...draft };
}

/** 获取指定行程日。 */
function requireDay(editor: ItineraryEditor, dayId: string): ItineraryDay {
  const day = editor.days.find((item) => item.id === dayId);
  if (!day) throw new Error(`找不到行程日：${dayId}`);
  return day;
}

/** 查找节点所在日、时段和槽内索引。 */
function requireNodeLocation(editor: ItineraryEditor, nodeId: string): MovePointTarget {
  for (const day of editor.days) {
    const node = day.nodes.find((item) => item.id === nodeId);
    if (!node) continue;
    return {
      dayId: day.id,
      timeSlot: node.timeSlot,
      index: day.nodes.filter((item) => item.timeSlot === node.timeSlot).findIndex((item) => item.id === nodeId),
    };
  }
  throw new Error(`找不到节点：${nodeId}`);
}

/** 按起始日期推导 ISO 日期。 */
function dateByOffset(startDate: string | null | undefined, offset: number): string | null {
  if (!startDate) return null;
  const date = new Date(`${startDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  date.setUTCDate(date.getUTCDate() + offset);
  return date.toISOString().slice(0, 10);
}

/** 返回两个 ISO 日期之间的整日差，无法解析时返回空值。 */
function dateDifference(from: string | null | undefined, to: string | null | undefined): number | null {
  if (!from || !to) return null;
  const fromTime = Date.parse(`${from}T00:00:00Z`);
  const toTime = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(fromTime) || !Number.isFinite(toTime)) return null;
  return Math.round((toTime - fromTime) / 86_400_000);
}

/** 保留原始时间后缀，仅平移 ISO 日期部分。 */
function shiftDateTime(value: string | null | undefined, dayDelta: number | null): string | null | undefined {
  if (!value || dayDelta === null || dayDelta === 0) return value;
  const datePart = value.slice(0, 10);
  const shiftedDate = dateByOffset(datePart, dayDelta);
  return shiftedDate ? `${shiftedDate}${value.slice(10)}` : value;
}

/** 平移交通方案中的出发/到达时间。 */
function shiftTransportSelection<T extends { departureAt?: string | null; arrivalAt?: string | null }>(
  value: T | null | undefined,
  dayDelta: number,
): T | null | undefined {
  if (!value) return value;
  return {
    ...value,
    departureAt: shiftDateTime(value.departureAt, dayDelta),
    arrivalAt: shiftDateTime(value.arrivalAt, dayDelta),
  };
}

/** 构造交通选项唯一键。 */
function selectionKey(selection: TransportSelection): string {
  return `${selection.provider}:${selection.optionId}`;
}

/** 创建节点后复用统一移动入口定位。 */
function insertNodeAt(
  editor: ItineraryEditor,
  target: MovePointTarget,
  draft: NodeDraft,
  idFactory: IdFactory,
  options?: { majorId?: string | null },
): ItineraryEditor {
  const next = structuredClone(editor);
  const id = idFactory();
  requireDay(next, target.dayId).nodes.push(createNode(id, target.timeSlot, draft));
  return movePoint(next, id, target, idFactory, options);
}

/** 校验并行轨道的端点、覆盖范围与专业段引用。 */
function validateParallelTracks(
  editor: ItineraryEditor,
  nodeIds: Set<string>,
  issues: ItineraryValidationIssue[],
): void {
  const segmentById = new Map(editor.segments.map((segment) => [segment.id, segment]));
  const overnightStays = railwayOvernightStayIds(editor.segments);
  for (const day of editor.days) {
    const tracks = day.parallelTracks?.tracks;
    if (!tracks?.length) continue;
    const nodeById = new Map(day.nodes.map((node) => [node.id, node]));
    const missingStart = new Set(tracks.map((track) => track.nodeIds[0])).size > 1;
    const missingEnd = new Set(tracks.map((track) => track.nodeIds.at(-1))).size > 1;
    const overnightBoundary = day.nodes.some((node) => overnightStays.has(node.id));
    if (!overnightBoundary && (missingStart || missingEnd || tracks.some((track) => track.nodeIds.length < 2))) {
      issues.push({ code: "parallel-track-endpoints-pending", detail: `第 ${day.dayNumber} 天并行考察点的公共${missingStart && !missingEnd ? "入口" : missingEnd && !missingStart ? "出口" : "首尾"}待补齐，请前插或后插住宿点、火车站或机场。` });
    }
    const covered = new Set<string>();
    for (const track of tracks) {
      const pathValid =
        track.nodeIds.length >= 1 &&
        track.segmentIds.length <= Math.max(0, track.nodeIds.length - 1);
      track.nodeIds.forEach((id) => covered.add(id));
      const trackNodes = track.nodeIds.map((id) => nodeById.get(id));
      if (trackNodes.every((node): node is ItineraryNode => Boolean(node))) {
        validateNodePositions(trackNodes, issues, `${day.id}:${track.id}`);
      }
      const pairIndexes = track.segmentIds.map((id) => (
        professionalSegmentPairIndex(day.id, track, segmentById.get(id))
      ));
      const segmentsValid = pairIndexes.every((index) => index >= 0)
        && new Set(track.segmentIds).size === track.segmentIds.length
        && new Set(pairIndexes).size === pairIndexes.length;
      if (!pathValid || !segmentsValid || track.nodeIds.some((id) => !nodeIds.has(id))) {
        issues.push({ code: "parallel-track-invalid", detail: `第 ${day.dayNumber} 天专业轨道不完整` });
      }
    }
    if (covered.size !== day.nodes.length || day.nodes.some((node) => !covered.has(node.id))) {
      issues.push({ code: "parallel-track-coverage-invalid", detail: `第 ${day.dayNumber} 天专业轨道未覆盖全部点位` });
    }
  }
}

/** 首尾和中间节点均允许住宿区域；中间仍禁止组织站点。 */
function validateNodePositions(
  nodes: ItineraryNode[],
  issues: ItineraryValidationIssue[],
  subjectPrefix: string,
): void {
  nodes.slice(1, -1)
    .filter((node) => node.nodeType === "organization_site")
    .forEach((node) => {
      issues.push({
        code: "intermediate-node-must-be-point",
        detail: "intermediate-node-must-be-point",
        subjectKey: `${subjectPrefix}:${node.id}`,
      });
    });
}
